import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
const Pricing = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const faqs = [{
    q: 'What AI search engines do you track?',
    a: 'Steward monitors all major AI search platforms including ChatGPT, Google AI Overviews, Perplexity, Gemini, Claude, and more. We continuously add new engines as they emerge.'
  }, {
    q: 'How quickly can I see results?',
    a: 'Most customers see measurable improvements in AI search visibility within 30-60 days of implementing our recommendations.'
  }, {
    q: 'Can I switch plans later?',
    a: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.'
  }, {
    q: 'Do you offer a free trial?',
    a: 'Yes! All plans come with a 14-day free trial. No credit card required to get started.'
  }];
  return <main className="flex-grow bg-gray-50" data-id="element-383">
      {/* Hero Section */}
      <div className="bg-primary pt-20 pb-32 text-center px-4 sm:px-6 lg:px-8" data-id="element-384">
        <div className="max-w-3xl mx-auto" data-id="element-385">
          <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl" data-id="element-386">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-6 text-xl text-textlight" data-id="element-387">
            Start optimizing for AI search today. Scale as you grow.
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10 pb-20" data-id="element-388">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-389">
          {/* Starter Plan */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm flex flex-col h-full" data-id="element-390">
            <h3 className="text-xl font-bold text-customblack mb-2" data-id="element-391">Starter</h3>
            <p className="text-customgray text-sm mb-6 h-10" data-id="element-392">
              For teams getting started with AEO/GEO
            </p>
            <div className="mb-6" data-id="element-393">
              <span className="text-4xl font-extrabold text-customblack" data-id="element-394">
                $100
              </span>
              <span className="text-customgray" data-id="element-395">/mo</span>
            </div>
            <Link to="/demo" className="w-full py-3 px-4 border border-gray-300 rounded-lg text-center font-medium text-customblack hover:bg-gray-50 transition-colors mb-8" data-id="element-396">
              Start Free Trial
            </Link>
            <div className="space-y-4 flex-grow" data-id="element-397">
              <p className="text-sm font-semibold text-customblack uppercase tracking-wider" data-id="element-398">
                Includes:
              </p>
              <ul className="space-y-3" data-id="element-399">
                {['1 brand monitored', '3 AI engines tracked', 'Weekly visibility reports', 'Basic content recommendations', 'Email support'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-400">
                    <CheckIcon className="w-5 h-5 text-accent mr-3 flex-shrink-0" data-id="element-401" />
                    <span className="text-customgray text-sm" data-id="element-402">{feature}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-white rounded-2xl border-2 border-accent p-8 shadow-xl relative flex flex-col h-full transform lg:-translate-y-4" data-id="element-403">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-404">
              <span className="bg-accent text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full" data-id="element-405">
                Most Popular
              </span>
            </div>
            <h3 className="text-xl font-bold text-customblack mb-2" data-id="element-406">Pro</h3>
            <p className="text-customgray text-sm mb-6 h-10" data-id="element-407">
              For growing teams serious about AI search
            </p>
            <div className="mb-6" data-id="element-408">
              <span className="text-4xl font-extrabold text-customblack" data-id="element-409">
                $300
              </span>
              <span className="text-customgray" data-id="element-410">/mo</span>
            </div>
            <Link to="/demo" className="w-full py-3 px-4 bg-accent rounded-lg text-center font-medium text-white hover:bg-cyan-400 transition-colors mb-8 shadow-md" data-id="element-411">
              Start Free Trial
            </Link>
            <div className="space-y-4 flex-grow" data-id="element-412">
              <p className="text-sm font-semibold text-customblack uppercase tracking-wider" data-id="element-413">
                Everything in Starter, plus:
              </p>
              <ul className="space-y-3" data-id="element-414">
                {['5 brands monitored', 'All AI engines tracked', 'Real-time visibility alerts', 'Advanced content optimization', 'Competitive intelligence', 'Priority support'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-415">
                    <CheckIcon className="w-5 h-5 text-accent mr-3 flex-shrink-0" data-id="element-416" />
                    <span className="text-customgray text-sm" data-id="element-417">{feature}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Enterprise Plan */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm flex flex-col h-full" data-id="element-418">
            <h3 className="text-xl font-bold text-customblack mb-2" data-id="element-419">
              Enterprise
            </h3>
            <p className="text-customgray text-sm mb-6 h-10" data-id="element-420">
              For large organizations with complex needs
            </p>
            <div className="mb-6" data-id="element-421">
              <span className="text-4xl font-extrabold text-customblack" data-id="element-422">
                Custom
              </span>
            </div>
            <Link to="/demo" className="w-full py-3 px-4 border border-gray-300 rounded-lg text-center font-medium text-customblack hover:bg-gray-50 transition-colors mb-8" data-id="element-423">
              Contact Sales
            </Link>
            <div className="space-y-4 flex-grow" data-id="element-424">
              <p className="text-sm font-semibold text-customblack uppercase tracking-wider" data-id="element-425">
                Everything in Pro, plus:
              </p>
              <ul className="space-y-3" data-id="element-426">
                {['Unlimited brands', 'All AI engines + custom', 'Custom integrations', 'Dedicated account manager', 'White-label reporting', 'SLA guarantee'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-427">
                    <CheckIcon className="w-5 h-5 text-accent mr-3 flex-shrink-0" data-id="element-428" />
                    <span className="text-customgray text-sm" data-id="element-429">{feature}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-430">
        <h2 className="text-3xl font-bold text-customblack text-center mb-10" data-id="element-431">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4" data-id="element-432">
          {faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-433">
              <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none" onClick={() => setOpenFaq(openFaq === index ? null : index)} data-id="element-434">
                <span className="font-semibold text-customblack" data-id="element-435">{faq.q}</span>
                {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-customgray" data-id="element-436" /> : <ChevronDownIcon className="w-5 h-5 text-customgray" data-id="element-437" />}
              </button>
              {openFaq === index && <div className="px-6 pb-4 text-customgray text-sm leading-relaxed" data-id="element-438">
                  {faq.a}
                </div>}
            </div>)}
        </div>
      </div>
    </main>;
};
export default Pricing;